import { signOut } from "firebase/auth";
import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import { auth, db, functions } from "../config/firebase";
import { enums, modules, componentMapping } from "../utils/enums";
import { similarityCheck } from "../utils/similarityCheck";
import createContext from "./createContext";

const appReducer = (state, action) => {
    switch (action.type) {
        case 'update_students':
            // Remove duplicates by uid
            const uniqueStudents = [...state.students];

            action.payload.forEach(newStudent => {
                const existingIndex = uniqueStudents.findIndex(student => student.uid === newStudent.uid);
                if (existingIndex >= 0) {
                    uniqueStudents[existingIndex] = newStudent;
                } else {
                    uniqueStudents.push(newStudent);
                }
            });
            return { ...state, students: uniqueStudents }
            
        case 'update_interns':
            // Remove duplicates by uid
            const uniqueInterns = [...state.interns];
            
            if (action.payload) {
                // Check if action.payload is an array, if not, wrap it in an array
                const payloadArray = Array.isArray(action.payload) ? action.payload : [action.payload];
                payloadArray.forEach(newIntern => {
                    const existingIndex = uniqueInterns.findIndex(intern => intern.uid === newIntern.uid);
                    if (existingIndex >= 0) {
                        // Merge new data with existing data, preserving existing fields
                        uniqueInterns[existingIndex] = {
                            ...uniqueInterns[existingIndex],  // Keep existing data
                            ...newIntern                      // Add/update with new data
                        };
                    } else {
                        uniqueInterns.push(newIntern);
                    }
                });
            }
            return { ...state, interns: uniqueInterns }
        case 'update_applicants':
            return { ...state, applicants: action.payload }
        case 'update_mentors':
            return { ...state, mentors: action.payload }
        case 'update_internships':
            // Handle both arrays and single objects
            if (!action.payload) {
                return { ...state, internships: [] };
            }
            
            // If payload is an array, replace the entire internships array
            if (Array.isArray(action.payload)) {
                return { ...state, internships: action.payload };
            }
            
            // If payload is a single object, add/update it in the existing array
            let updatedInternships = [...(state.internships || [])];
            const internshipIndex = updatedInternships.findIndex(internship => internship.uid === action.payload.uid);
            
            if (isFinite(internshipIndex) && internshipIndex >= 0) {
                // Update existing internship
                updatedInternships[internshipIndex] = {
                    ...updatedInternships[internshipIndex],
                    ...action.payload
                };
            } else {
                // Add new internship
                updatedInternships.push(action.payload);
            }
            
            return { ...state, internships: updatedInternships };
        case 'update_internship':
            let newInternships = [...(state.internships || [])];
            if (action.payload) {
                // Check if action.payload is an array, if not, wrap it in an array
                const payloadArray = Array.isArray(action.payload) ? action.payload : [action.payload];
                
                payloadArray.forEach(newInternship => {
                    const internshipIndex = newInternships.findIndex(internship => internship.uid === newInternship.uid);
                    if (isFinite(internshipIndex) && internshipIndex >= 0) {
                        newInternships[internshipIndex] = {
                            ...newInternships[internshipIndex],  // Keep existing data
                            ...newInternship                     // Add/update with new data
                        };
                    } else {
                        newInternships.push(newInternship);
                    }
                });
            }
            return { ...state, internships: newInternships }
        case 'update_user':
            return { ...state, currentUser: action.payload }
        case 'update_ideas':
            return { ...state, ideas: action.payload }
        case 'update_messages':
            return { ...state, messages: action.payload }
        case 'update_idea':
            let newIdeas = [...state.ideas]
            const index = newIdeas?.findIndex(idea => idea.uid === action.payload.uid);
            if (isFinite(index) && index >= 0) {
                newIdeas[index] = action.payload;
            } else {
                newIdeas = [...newIdeas, action.payload];
            }
            return { ...state, ideas: newIdeas }
        case 'update_gpt':
            let newGPTs = [...state.GPTS]
            const gptIndex = newGPTs?.findIndex(gpt => gpt.uid === action.payload.uid);
            if (isFinite(gptIndex) && gptIndex >= 0) {
                newGPTs[gptIndex] = action.payload;
            } else {
                newGPTs = [...newGPTs, action.payload];
            }
            return { ...state, GPTS: newGPTs }
        case 'update_gpts':
            return { ...state, GPTS: [...action.payload] }
        case 'delete_gpt':
            const gptsWithoutDeleted = state.GPTS.filter(gpt => gpt.uid !== action.payload);
            return { ...state, GPTS: gptsWithoutDeleted }
        case 'create_gpt':
            const createdGptAndNewGPTs = [...state.GPTS, action.payload];
            return { ...state, GPTS: createdGptAndNewGPTs }
        case 'delete_internship':
            const internshipsWithoutDeleted = state.internships.filter(internship => internship.uid !== action.payload);
            return { ...state, internships: internshipsWithoutDeleted }

        default:
            return state;
    }
}

const createOrUpdateIdea = (dispatch) => {
    return async (data) => {
        try {
            await setDoc(doc(db, "ideas", data.uid), data);
            dispatch({ type: 'update_idea', payload: data });
            toast.success("Idea uploaded successfully.")
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const updateIdeas = (dispatch) => {
    return async (data) => {
        try {
            dispatch({ type: 'update_ideas', payload: data });
            toast.success("Idea uploaded successfully.")
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const updateMessages = (dispatch) => {
    return async (data) => {
        try {
            dispatch({ type: 'update_messages', payload: data });
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const fetchIdeas = (dispatch) => {
    return async () => {
        try {
            let items = [];
            const querySnapshot = await getDocs(collection(db, "ideas"));
            querySnapshot.forEach((doc) => {
                items.push({
                    uid: doc.uid,
                    ...doc?.data()
                });
            });
            items = items.sort((a, b) => b.createdAt - a.createdAt);
            dispatch({ type: "update_ideas", payload: items });
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const getUserData = (dispatch) => {
    return async () => {
        try {
            const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (!userDoc.data()) return signOut(auth)
            dispatch({ type: "update_user", payload: userDoc.data() });

        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const getMentors = (dispatch) => {
    return async () => {
        const mentorsQuery = query(
            collection(db, "users"),
            where("role", "==", enums.MENTOR),
            where("isMentor", "==", true)
        );
        const mentorsSnapshot = await getDocs(mentorsQuery);

        let mentorsData = []
        mentorsSnapshot.docs.map(doc => (
            mentorsData.push(
                {
                    uid: doc.data().uid,
                    name: doc.data().name,
                    jobTitle: doc.data().jobTitle,
                    jobDescription: doc.data().jobDescription,
                    location: doc.data().location,
                    company: doc.data().company,
                    linkedIn: doc.data().linkedIn,
                    email: doc.data().email,
                    mentees: doc.data().mentees || [], // Ensure mentees is always an array
                    isMentor: doc.data().isMentor,
                })))

        dispatch({ type: "update_mentors", payload: mentorsData });
    }
}

const getInterns = (dispatch) => {
    return async (internUids) => {
        try {
            if (!internUids || !internUids.length) {
                dispatch({ type: "update_interns", payload: [] });
                return;
            }

            // Get all student documents where uid is in the studentUids array
            const internQuery = query(
                collection(db, "users"),
                where("role", "==", enums.INTERN),
                where("uid", "in", internUids)
            );

            const internSnapshot = await getDocs(internQuery);

            let internsData = []
            internSnapshot.docs.map(doc => (
                internsData.push({
                    uid: doc.data().uid,
                    name: doc.data().name,
                    email: doc.data().email,
                    jobTitle: doc.data().jobTitle,
                    jobDescription: doc.data().jobDescription,
                    location: doc.data().location,
                    school: doc.data().school,
                    internshipUid: doc.data().internshipUid
                }))
            );

            dispatch({ type: "update_interns", payload: internsData });
        } catch (err) {
            console.error('Error fetching interns:', err);
            toast.error(err.message || 'Failed to fetch interns');
            dispatch({ type: "update_interns", payload: [] });
        }
    }
}

const updateInternship = (dispatch) => {
    return async (data) => {
        const uid = data.uid
        await updateDoc(doc(db, "internships", uid), data);
        dispatch({ type: "update_internship", payload: data });
    }
}

const getApplicants = (dispatch) => {
    return async (currentUserUid) => {
        try {
            if (!currentUserUid) {
                dispatch({ type: "update_applicants", payload: [] });
                return;
            }

            // Get all student documents where uid is in the studentUids array
            const applicantsquery = query(
                collection(db, "users"),
                where("role", "==", enums.INTERN),
                where("mentorApplications", "array-contains", currentUserUid)
            );

            const studentsSnapshot = await getDocs(applicantsquery);

            let applicantsData = []
            studentsSnapshot.docs.map(doc => (
                applicantsData.push({
                    uid: doc.data().uid,
                    name: doc.data().name,
                    jobTitle: doc.data().jobTitle,
                    jobDescription: doc.data().jobDescription,
                    location: doc.data().location,
                    school: doc.data().school,
                    interests: doc.data().interests,
                    age: doc.data().age,
                }))
            );

            dispatch({ type: "update_applicants", payload: applicantsData });
        } catch (err) {
            console.error('Error fetching students:', err);
            toast.error(err.message || 'Failed to fetch students');
            dispatch({ type: "update_students", payload: [] });
        }
    }
}

const getInternships = (dispatch) => {
    return async (data) => {
        try {
            if (!data?.uid) {
                dispatch({ type: "update_internships", payload: [] });
                return;
            }
            // Create query based on user type
            const internshipsQuery = query(
                collection(db, "internships"),
                where(data.type === enums.MENTOR ? 'mentorUid' : 'studentUid', "==", data.uid)
            );

            const snapshot = await getDocs(internshipsQuery);
            let internshipsData = [];
            snapshot.docs.forEach(doc => {
                internshipsData.push({ ...doc.data() });
            });

            dispatch({ type: "update_internships", payload: internshipsData });
        } catch (err) {
            console.error('Error fetching internships:', err);
            toast.error(err.message || 'Failed to fetch internships');
            dispatch({ type: "update_internships", payload: [] });
        }
    }
}

const updateUserData = (dispatch) => {
    return async (data) => {
        try {
            
            // Get current user data first
            const userRef = doc(db, "users", auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
                const currentUserData = userDoc.data();
                
                // Merge the new data with existing data
                const updatedData = {
                    ...currentUserData,
                    ...data
                };
                
                // Update in Firestore
                await updateDoc(userRef, data);
                
                // Update in state with complete merged data
                dispatch({ type: "update_user", payload: updatedData });
            } else {
                // If user doc doesn't exist, just update with the provided data
                await updateDoc(userRef, data);
                dispatch({ type: "update_user", payload: data });
            }
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}


const updateSomeUserData = (dispatch) => {
    return async (data) => {
        try {
            
            const userRef = doc(db, "users", data.uid);
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
                const currentUserData = userDoc.data();
                
                // Merge the new data with existing data
                const updatedData = {
                    ...currentUserData,
                    ...data
                };
                
                // Update in Firestore
                await updateDoc(userRef, data);
                
                // Update in state with complete merged data
                dispatch({ type: "update_user", payload: updatedData });
            } else {
                // If user doc doesn't exist, just update with the provided data
                await updateDoc(userRef, data);
                dispatch({ type: "update_user", payload: data });
            }
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const updateInternData = (dispatch) => {
    return async (data) => {
        try {
            await updateDoc(doc(db, "users", data.uid), data);
            dispatch({ type: "update_interns", payload: data });
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const createInternship = (dispatch) => {
    return async (data) => {
        try {
            await setDoc(doc(db, "internships", data.uid), data);
            dispatch({ type: "update_internships", payload: data });
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const updateIdeaValues = (dispatch) => {
    return async (data) => {
        try {
            dispatch({ type: 'update_idea', payload: data });
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed please try again!");
        }
    }
}

const deleteInternship = (dispatch) => {
    return async (uid) => {
        try {
            // First, get the internship data to find mentorUid and studentUid
            const internshipDoc = await getDoc(doc(db, "internships", uid));
            
            if (!internshipDoc.exists()) {
                throw new Error("Internship not found");
            }
            
            const internshipData = internshipDoc.data();
            const { mentorUid, studentUid } = internshipData;
            
            // Create a batch for atomic updates
            const batch = writeBatch(db);
            
            // Update mentor: remove intern from mentees list
            if (mentorUid) {
                const mentorRef = doc(db, "users", mentorUid);
                const mentorDoc = await getDoc(mentorRef);
                
                if (mentorDoc.exists()) {
                    const mentorData = mentorDoc.data();
                    const updatedMentees = (mentorData.mentees || []).filter(menteeUid => menteeUid !== studentUid);
                    batch.update(mentorRef, { mentees: updatedMentees });
                }
            }
            
            // Update intern: reset internship-related fields
            if (studentUid) {
                const internRef = doc(db, "users", studentUid);
                batch.update(internRef, {
                    internshipUid: null,
                    hasApplied: false,
                    mentorUid: null,
                    mentorApplications: []
                });
            }
            
            // Delete the internship document
            batch.delete(doc(db, "internships", uid));
            
            // Commit all updates
            await batch.commit();
            
            // Update state by removing the deleted internship
            dispatch({ type: 'delete_internship', payload: uid });
            
            toast.success("Internship deleted successfully");
        } catch (err) {
            toast.error(err.message || "Failed to delete internship");
            console.error(err);
            throw Error("Failed to delete internship. Please try again!");
        }
    }
}

const runSimilarityCheck = (dispatch) => {
    return async (id) => {
        try {
            toast.info("Running similarity check please wait...")
            let items = [];
            const querySnapshot = await getDocs(collection(db, "ideas"));
            querySnapshot.forEach((doc) => {
                if (doc.uid !== id) {
                    items.push({
                        uid: doc.uid,
                        ...doc?.data()
                    });
                }
            });
            const docRef = doc(db, "ideas", id);
            const docSnap = await getDoc(docRef);
            console.log(items)
            if (docSnap.exists()) {
                const primaryIdea = docSnap.data();
                const ideas = await similarityCheck(primaryIdea, items);
                console.log("similarity check", ideas)
                const batch = writeBatch(db);
                for (let i = 0; i < ideas?.length; i++) {
                    const idea = ideas[i];
                    const ref = doc(db, "ideas", idea?.uid);
                    batch.update(ref, { ...idea })
                }
                await batch.commit();
                fetchIdeas(dispatch)();

                toast.success("Similarity check is done.")
            } else {
                // docSnap.data() will be undefined in this case
                console.log("No such document!");
                throw Error("Idea not found")
            }
        } catch (err) {
            toast.error(err?.message || err);
            // throw Error("Failed please try again!");
        }
    }
}

const createGPT = (dispatch) => {
    return async (data) => {
        try {
            const gptData = {
                ...data,
                createdBy: auth.currentUser.uid,
                createdAt: new Date().getTime()
            };
            await setDoc(doc(db, "gpts", data.uid), gptData);
            dispatch({ type: 'create_gpt', payload: gptData });
            toast.success("GPT created successfully.");
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed to create GPT. Please try again!");
        }
    }
}

const updateGPT = (dispatch) => {
    return async (data) => {
        try {
            await updateDoc(doc(db, "gpts", data.uid), data);
            dispatch({ type: 'update_gpt', payload: data });
            toast.success("GPT updated successfully.");
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed to update GPT. Please try again!");
        }
    }
}

const deleteGPT = (dispatch) => {
    return async (uid) => {
        try {
            await deleteDoc(doc(db, "gpts", uid));
            dispatch({ type: 'delete_gpt', payload: uid });
            toast.success("GPT deleted successfully.");
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed to delete GPT. Please try again!");
        }
    }
}

const fetchGPTs = (dispatch) => {
    return async () => {
        try {
            let items = [];
            // Add query to filter by current user's uid
            const gptsQuery = query(
                collection(db, "gpts"),
                where("createdBy", "==", auth.currentUser.uid)
            );
            const querySnapshot = await getDocs(gptsQuery);
            querySnapshot.forEach((doc) => {
                items.push({
                    uid: doc.id,
                    ...doc?.data()
                });
            });
            dispatch({ type: "update_gpts", payload: items });
        } catch (err) {
            toast.error(err);
            console.log(err);
            throw Error("Failed to fetch GPTs. Please try again!");
        }
    }
}

const setGPTs = (dispatch) => {
    return async (data) => {
        dispatch({ type: "update_gpts", payload: data });
    }
}

// Admin functions to get all data
const getAllInternships = (dispatch) => {
    return async () => {
        try {
            console.log('getting all internships for admin');
            const snapshot = await getDocs(collection(db, "internships"));
            let internshipsData = [];
            snapshot.docs.forEach(doc => {
                internshipsData.push({ ...doc.data() });
            });
            dispatch({ type: "update_internships", payload: internshipsData });
        } catch (err) {
            console.error('Error fetching all internships:', err);
            toast.error(err.message || 'Failed to fetch internships');
            dispatch({ type: "update_internships", payload: [] });
        }
    }
}

const getAllInterns = (dispatch) => {
    return async () => {
        try {
            console.log('getting all interns for admin');
            const internsQuery = query(
                collection(db, "users"),
                where("role", "==", enums.INTERN)
            );
            const snapshot = await getDocs(internsQuery);
            let internsData = [];
            snapshot.docs.forEach(doc => {
                internsData.push({
                    uid: doc.data().uid,
                    name: doc.data().name,
                    email: doc.data().email,
                    jobTitle: doc.data().jobTitle,
                    jobDescription: doc.data().jobDescription,
                    location: doc.data().location,
                    school: doc.data().school,
                    interests: doc.data().interests,
                    mentorApplications: doc.data().mentorApplications,
                    internshipUid: doc.data().internshipUid
                });
            });
            dispatch({ type: "update_interns", payload: internsData });
        } catch (err) {
            console.error('Error fetching all interns:', err);
            toast.error(err.message || 'Failed to fetch interns');
            dispatch({ type: "update_interns", payload: [] });
        }
    }
}

const getAllMentors = (dispatch) => {
    return async () => {
        try {
            console.log('getting all mentors for admin');
            const mentorsQuery = query(
                collection(db, "users"),
                where("role", "==", enums.MENTOR)
            );
            const snapshot = await getDocs(mentorsQuery);
            let mentorsData = [];
            snapshot.docs.forEach(doc => {
                mentorsData.push({
                    uid: doc.data().uid,
                    name: doc.data().name,
                    jobTitle: doc.data().jobTitle,
                    jobDescription: doc.data().jobDescription,
                    location: doc.data().location,
                    company: doc.data().company,
                    linkedIn: doc.data().linkedIn,
                    isMentor: doc.data().isMentor,
                    mentees: doc.data().mentees
                });
            });
            dispatch({ type: "update_mentors", payload: mentorsData });
        } catch (err) {
            console.error('Error fetching all mentors:', err);
            toast.error(err.message || 'Failed to fetch mentors');
            dispatch({ type: "update_mentors", payload: [] });
        }
    }
}

// Send matched emails to mentor and mentee
const sendMatchedEmails = (dispatch) => {
    return async (mentorEmail, mentorName, menteeEmail, menteeName, schoolName) => {
        try {
            const sendMatchedEmailsFunction = httpsCallable(functions, 'sendMatchedEmails');
            await sendMatchedEmailsFunction({
                mentorEmail,
                mentorName,
                menteeEmail,
                menteeName,
                schoolName
            });
            toast.success('Matched emails sent successfully!');
        } catch (error) {
            console.error('Error sending matched emails:', error);
            toast.error('Failed to send matched emails');
            throw error;
        }
    }
}

// Send availability request email
const sendAvailabilityRequest = (dispatch) => {
    return async (menteeEmail, menteeName, mentorName) => {
        try {
            // For now, we'll use the same function but could create a separate one
            toast.info('Availability request sent to mentee!');
        } catch (error) {
            console.error('Error sending availability request:', error);
            toast.error('Failed to send availability request');
            throw error;
        }
    }
}

// Schedule meeting with Google Meet
const scheduleMeeting = (dispatch) => {
    return async (mentorEmail, menteeEmail, meetingTitle, meetingDate, dayOfWeek, hour, mentorName, menteeName, timezone) => {
        try {
            const scheduleMeetingFunction = httpsCallable(functions, 'scheduleMeeting');
            const result = await scheduleMeetingFunction({
                mentorEmail,
                menteeEmail,
                meetingTitle,
                meetingDate,
                dayOfWeek,
                hour,
                mentorName,
                menteeName,
                timezone
            });

            toast.success(result.data.message);
            return result.data;
        } catch (error) {
            console.error('Error scheduling meeting:', error);
            toast.error('Failed to schedule meeting');
            throw error;
        }
    }
}

// Cancel meeting
const cancelMeeting = (dispatch) => {
    return async (eventId) => {
        try {
            const cancelMeetingFunction = httpsCallable(functions, 'cancelMeeting');
            const result = await cancelMeetingFunction({
                eventId
            });

            toast.success(result.data.message);
            return result.data;
        } catch (error) {
            console.error('Error cancelling meeting:', error);
            toast.error('Failed to cancel meeting');
            throw error;
        }
    }
}


export const { Provider, Context } = createContext(
    appReducer,
    {
        fetchIdeas,
        createOrUpdateIdea,
        updateIdeaValues,
        runSimilarityCheck,
        updateIdeas,
        updateMessages,
        getUserData,
        updateUserData,
        getMentors,
        getInterns,
        getApplicants,
        createInternship,
        getInternships,
        updateInternship,
        updateInternData,
        createGPT,
        updateGPT,
        deleteGPT,
        fetchGPTs, 
        setGPTs,
        getAllInternships,
        getAllInterns,
        getAllMentors,
        deleteInternship,
        updateSomeUserData,
        sendMatchedEmails,
        sendAvailabilityRequest,
        scheduleMeeting,
        cancelMeeting
    },
    {
        currentUser: null,
        mentors: [],
        interns: [],
        internships: [],
        applicants: [],
        students: [],
        ideas: [],
        messages: [],
        GPTS: [],
        modules: modules,
        componentMapping: componentMapping
    }
)


